// client/src/features/auth/VerifyEmail.tsx — „Confirmarea adresei" · D-2759.
//
// The page the confirmation mail opens. The server composes `{origin}/verify-email/{token}`
// (`EmailVerificationMailer.VerifyRoute`), and adds `?next=<path>` when the signup or the resend
// carried a return path (D-2753) — so the spelling is the server's, and the route file beside
// this one answers exactly it.
//
// The token is spent on arrival, once: there is no „Confirmă" button, because opening the link
// IS the confirmation, and a second click would only be a second place to abandon the flow. The
// call is idempotent server-side (the same link confirms twice), so the ref below is not
// guarding a single-use credential the way `AcceptInvitation`'s is — it keeps React's doubled
// effect from racing two requests into two renders of the same answer.
//
// Four states:
//
//   • confirmed — the address, and the way on. The confirmation issues NO session (D-2752): the
//     person signs in with their password, and every rule of the login applies then — MFA, the
//     lease linked by address (D-1910), the landing `landingFor` picks. So the button is
//     „Autentificați-vă", carrying `next` as `redirect` and, for an invitation, its token as `invite`, so
//     the login form fills in and locks the address the invitation names (D-1930, D-2762). A
//     visitor who already holds a session — the link opened in the tab they were working in —
//     is offered „Continuați" to where the login would have taken them instead.
//
//   • invalid — forged, expired after 48 hours, or naming an address the account no longer has:
//     the server gives one answer for the three and so does this page. It offers a new link on
//     the spot: an address field and the resend, carrying the same `next` (D-2762) and waiting
//     out the server's minute (D-2761). The answer is 202 whatever the address, so what follows
//     is the neutral sentence every resend shows.
//
//   • failed — nothing came back, or the per-caller limiter said no. The link is still good, so
//     the page says what happened and offers to try again.
//
//   • verifying — the moment in between.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { authApi } from '@/api/auth';
import { FormField } from '@/components/forms/FormField';
import { Glyph } from '@/components/ui/Glyph';
import { zEmail } from '@/lib/validation';
import { getSession, useSessionStatus } from '@/session';

import { problemCode, useAuthErrorMessage } from './errors';
import {
  invitationTokenOf,
  landingFor,
  returnPath,
  safeRedirect,
  type RedirectSearch,
} from './redirect';
import { useResendVerification } from './ResendVerification';

/** The search params `/verify-email/$token` accepts: the return path the mail carried (D-2753). */
export const verifyEmailSearchSchema = z.object({
  next: z.string().optional(),
});

export type VerifyEmailSearch = z.infer<typeof verifyEmailSearchSchema>;

type VerifyState =
  | { kind: 'verifying' }
  | { kind: 'verified'; email: string }
  | { kind: 'invalid' }
  | { kind: 'failed'; message: string };

/**
 * What the login is handed after a confirmation (D-2762): `next` as `redirect` when the login
 * would follow it, and — for an invitation — its token as `invite`, the value D-1930 locks the
 * address field with. The address travels as the token and never as itself, as everywhere else.
 */
function loginSearchFor(next: string | undefined): RedirectSearch {
  const redirect = returnPath(next);
  const invite = invitationTokenOf(redirect);

  return {
    ...(redirect === undefined ? {} : { redirect }),
    ...(invite === undefined ? {} : { invite }),
  };
}

export function VerifyEmail({ token, search }: { token: string; search: VerifyEmailSearch }) {
  const { t } = useTranslation();
  const describe = useAuthErrorMessage();
  const [state, setState] = useState<VerifyState>({ kind: 'verifying' });

  const verify = useCallback(async () => {
    try {
      const verified = await authApi.verifyEmail(token);
      setState({ kind: 'verified', email: verified.email });
    } catch (error) {
      setState(
        problemCode(error) === 'email-verification-invalid'
          ? { kind: 'invalid' }
          : { kind: 'failed', message: describe(error) },
      );
    }
  }, [describe, token]);

  // Once per token, whatever React does with the effect — see the header.
  const attempted = useRef<string | null>(null);

  useEffect(() => {
    if (attempted.current === token) return;
    attempted.current = token;
    void verify();
  }, [token, verify]);

  const retry = () => {
    setState({ kind: 'verifying' });
    void verify();
  };

  switch (state.kind) {
    case 'verifying':
      return (
        <p className="text-sm text-gray-500" role="status">
          {t('auth.verifyEmail.verifying')}
        </p>
      );

    case 'verified':
      return <Verified email={state.email} next={search.next} />;

    case 'invalid':
      return <InvalidLink next={search.next} />;

    case 'failed':
      return (
        <div className="space-y-4">
          <Glyph icon="exclamation-triangle" tone="warning" size="lg" />
          <h1 className="text-2xl font-bold text-gray-800">{t('auth.verifyEmail.failedTitle')}</h1>
          <div className="alert alert-error text-sm" role="alert">
            {state.message}
          </div>
          <button type="button" className="btn btn-primary" onClick={retry}>
            {t('common.retry')}
          </button>
        </div>
      );
  }
}

/** The confirmed state: the address, and the way on — to the login, or on with the session held. */
function Verified({ email, next }: { email: string; next: string | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const status = useSessionStatus();

  // The route's `beforeLoad` bootstrapped the session before this rendered, so `status` is
  // already known; subscribing to it re-renders this state if the session ends meanwhile.
  const session = status === 'authenticated' ? getSession() : null;

  return (
    <div className="space-y-4">
      <Glyph icon="check-circle" tone="success" size="lg" />
      <div>
        <h1 className="text-2xl font-bold text-gray-800">{t('auth.verifyEmail.verifiedTitle')}</h1>
        <p className="mt-1 text-sm text-pretty text-gray-500" role="status">
          {session === null
            ? t('auth.verifyEmail.verifiedBody', { email })
            : t('auth.verifyEmail.verifiedBodySignedIn', { email })}
        </p>
      </div>

      {session === null ? (
        <Link to="/login" search={loginSearchFor(next)} className="btn btn-primary w-full">
          {t('auth.verifyEmail.signIn')}
        </Link>
      ) : (
        // Where the login would have gone with this deep link and this account (D-1732). A
        // button and `navigate({ href })` rather than a `<Link to>`, because the deep link may
        // carry a query of its own, which `to` would read as part of the path.
        <button
          type="button"
          className="btn btn-primary w-full"
          onClick={() => void navigate({ href: safeRedirect(next, landingFor(session)) })}
        >
          {t('auth.verifyEmail.continue')}
        </button>
      )}
    </div>
  );
}

const resendSchema = z.object({ email: zEmail });
type ResendValues = z.infer<typeof resendSchema>;

/**
 * The spent link, and a new one on the spot (D-2759). The address is typed rather than known:
 * the token that would name it is exactly what the server just refused to read.
 */
function InvalidLink({ next }: { next: string | undefined }) {
  const { t } = useTranslation();
  const { resend, sending, sent, failure, secondsLeft } = useResendVerification(returnPath(next));

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResendValues>({ resolver: zodResolver(resendSchema), defaultValues: { email: '' } });

  const onSubmit = handleSubmit(async (values) => {
    await resend(values.email);
  });

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="space-y-4" noValidate>
      <Glyph icon="x-circle" tone="danger" size="lg" />
      <div>
        <h1 className="text-2xl font-bold text-gray-800">{t('auth.verifyEmail.invalidTitle')}</h1>
        <p className="mt-1 text-sm text-pretty text-gray-500">
          {t('auth.verifyEmail.invalidLead')}
        </p>
      </div>

      <div className="alert alert-error text-sm" role="alert">
        {t('auth.errors.emailVerificationInvalid')}
      </div>

      <FormField label={t('auth.common.email')} required error={errors.email?.message}>
        {(aria) => (
          <input
            {...aria}
            {...register('email')}
            type="email"
            autoComplete="email"
            inputMode="email"
            className="input input-bordered w-full"
          />
        )}
      </FormField>

      {/* Disabled while the server's minute runs, counting it down on the label (D-2761). */}
      <button
        type="submit"
        className="btn btn-primary w-full"
        disabled={sending || secondsLeft > 0}
      >
        {secondsLeft > 0
          ? t('auth.verifyEmail.resendIn', { seconds: secondsLeft })
          : t('auth.verifyEmail.resend')}
      </button>

      {sent ? (
        <p className="text-sm text-pretty text-gray-600" role="status">
          {t('auth.verifyEmail.resent')}
        </p>
      ) : null}

      {failure ? (
        <div className="alert alert-error text-sm" role="alert">
          {failure}
        </div>
      ) : null}

      <p className="text-center text-sm">
        <Link to="/login" search={loginSearchFor(next)} className="link link-primary">
          {t('auth.common.backToLogin')}
        </Link>
      </p>
    </form>
  );
}
